import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EbookCard from "./EbookCard";
import { fetchEbookCategories, fetchEbooks } from "../../api/ebookApi";

/** 读书会页面内的「电子书」区块（无独立页头/导航） */
export default function EbookLibrarySection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = localStorage.getItem("user_id") || "";

  const categoryId = searchParams.get("category_id") || "";
  const appliedKeyword = searchParams.get("keyword") || "";

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [keyword, setKeyword] = useState(appliedKeyword);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setKeyword(appliedKeyword);
  }, [appliedKeyword]);

  useEffect(() => {
    fetchEbookCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchEbooks({
      page,
      pageSize,
      categoryId,
      keyword: appliedKeyword,
      userId,
    })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, categoryId, appliedKeyword, userId]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "ebooks");
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    updateParams({ keyword: keyword.trim() || null });
  };

  const selectCategory = (id) => {
    setPage(1);
    updateParams({ category_id: id || null });
  };

  return (
    <section className="space-y-8">
      <header className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-800">📚 电子书</h2>
        <p className="text-gray-600 text-sm max-w-2xl mx-auto">
          精选婚恋、心理、沟通、家庭成长读物。注册会员可免费下载，游客可浏览封面与简介。
        </p>
      </header>

      <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex gap-2">
        <input
          type="search"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索电子书、作者、标签..."
          className="flex-1 rounded-full border border-gray-200 px-5 py-3 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
        />
        <button
          type="submit"
          className="px-6 py-3 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-600"
        >
          🔍 搜索
        </button>
      </form>

      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => selectCategory("")}
          className={`px-4 py-2 rounded-full text-sm border ${
            !categoryId
              ? "bg-pink-50 border-pink-400 text-pink-600"
              : "bg-white border-gray-200 text-gray-500"
          }`}
        >
          全部
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => selectCategory(String(cat.id))}
            className={`px-4 py-2 rounded-full text-sm border ${
              String(categoryId) === String(cat.id)
                ? "bg-pink-50 border-pink-400 text-pink-600"
                : "bg-white border-gray-200 text-gray-500"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white p-12 rounded-2xl shadow text-center text-gray-400">
          正在加载电子书...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow text-center text-gray-400">
          暂无匹配的电子书
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <EbookCard key={item.id} ebook={item} userId={userId} />
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <div className="flex justify-center items-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-full border text-sm disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-sm text-gray-500">
            第 {page} / {totalPages} 页 · 共 {total} 本
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-4 py-2 rounded-full border text-sm disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      ) : null}
    </section>
  );
}
